import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useDiagnosticCodes, type CarePresentation } from "@/lib/api/care";
import { useDebounce } from "@/lib/hooks/use-debounce";
import { ArrowRight, Loader2, Search } from "lucide-react";
import { useState } from "react";
import { CodeChip } from "./care-primitives";

interface FindPresentationDialogProps {
  open: boolean;
  current: CarePresentation | null;
  onClose: () => void;
  onSelect: (presentation: CarePresentation) => void;
}

export function FindPresentationDialog({
  open,
  current,
  onClose,
  onSelect,
}: FindPresentationDialogProps) {
  const [picked, setPicked] = useState<CarePresentation | null>(null);
  const close = () => {
    setPicked(null);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-w-2xl rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-text-primary">
            {picked ? "Confirm presentation" : "Find a Presentation"}
          </DialogTitle>
        </DialogHeader>
        {picked ? (
          <ConfirmStep
            current={current}
            next={picked}
            onBack={() => setPicked(null)}
            onNext={() => {
              onSelect(picked);
              setPicked(null);
            }}
          />
        ) : (
          <SearchStep onPick={setPicked} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function SearchStep({ onPick }: { onPick: (p: CarePresentation) => void }) {
  const [query, setQuery] = useState("");
  const debounced = useDebounce(query.trim(), 300);
  const codes = useDiagnosticCodes(debounced);
  const items = codes.data?.pages.flatMap((page) => page.codes) ?? [];

  return (
    <div className="flex flex-col gap-3">
      <Input
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search by code or diagnosis"
        startIcon={<Search className="size-4 text-text-tertiary" />}
        className="h-11 rounded-xl border-[#dfddd8] pl-8 font-body text-base"
      />
      <div
        className="max-h-[50vh] overflow-y-auto rounded-2xl border border-[#dfddd8]"
        onScroll={(event) => {
          const el = event.currentTarget;
          const nearBottom =
            el.scrollTop + el.clientHeight >= el.scrollHeight - 80;
          if (nearBottom && codes.hasNextPage && !codes.isFetchingNextPage) {
            codes.fetchNextPage();
          }
        }}
      >
        {codes.isPending ? (
          <Centered>
            <Loader2 className="size-5 animate-spin text-text-tertiary" />
          </Centered>
        ) : items.length === 0 ? (
          <Centered>
            <p className="font-body text-sm text-text-secondary">
              No presentations match.
            </p>
          </Centered>
        ) : (
          items.map((item) => (
            <button
              key={item.code}
              type="button"
              onClick={() => onPick(item)}
              className="flex w-full items-center gap-3 border-b border-[#dfddd8] px-4 py-3 text-left last:border-b-0 hover:bg-surfaces-z3"
            >
              <span className="w-20 shrink-0 font-mono text-sm text-text-secondary">
                {item.code}
              </span>
              <span className="font-body text-base leading-6 text-text-primary">
                {item.description}
              </span>
            </button>
          ))
        )}
        {codes.isFetchingNextPage ? (
          <Centered>
            <Loader2 className="size-4 animate-spin text-text-tertiary" />
          </Centered>
        ) : null}
      </div>
    </div>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex justify-center px-4 py-6">{children}</div>;
}

function ConfirmStep({
  current,
  next,
  onBack,
  onNext,
}: {
  current: CarePresentation | null;
  next: CarePresentation;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-4">
        <PresentationCard label="Current presentation" presentation={current} />
        <ArrowRight className="size-5 text-text-tertiary" aria-hidden />
        <PresentationCard label="New presentation" presentation={next} />
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex h-10 items-center justify-center rounded-full border border-[#dfddd8] bg-white px-6 font-heading text-sm font-bold text-text-primary"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onNext}
          className="inline-flex h-10 items-center justify-center rounded-full bg-black px-6 font-heading text-sm font-bold text-white"
        >
          Next
        </button>
      </div>
    </div>
  );
}

function PresentationCard({
  label,
  presentation,
}: {
  label: string;
  presentation: CarePresentation | null;
}) {
  return (
    <div className="flex min-h-[112px] flex-col gap-2 rounded-2xl border border-[#dfddd8] bg-surfaces-z3 p-4">
      <p className="font-body text-xs text-text-secondary">{label}</p>
      {presentation ? (
        <>
          <CodeChip code={presentation.code} />
          <p className="font-heading text-base font-bold leading-6 text-text-primary">
            {presentation.description}
          </p>
        </>
      ) : (
        <p className="font-heading text-base font-bold leading-6 text-[#6e6b67]">
          Not yet assigned
        </p>
      )}
    </div>
  );
}
